'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconTileType, SalAttrs } from '../../../types';

export type IconTilesProps = {
    dark?: boolean;
    content: IconTileType[];
    titleClasses?: string;
    subtitleClass?: string;
    titleSal?: SalAttrs;
    subtitleSal?: SalAttrs;
    iconSal?: SalAttrs;
};

const ICON_TILE_MIN_WIDTH = 176;
const ICON_TILE_DEFAULT_COLUMNS = 4;
const ICON_TILE_DEFAULT_GAP = 32;

export const getIconTileMaxColumns = (
    containerWidth: number,
    itemCount: number,
    gap = ICON_TILE_DEFAULT_GAP,
    minTileWidth = ICON_TILE_MIN_WIDTH,
) => {
    if (itemCount <= 0) return 1;
    if (containerWidth <= 0 || minTileWidth <= 0) return 1;

    const measuredColumns = Math.floor((containerWidth + gap) / (minTileWidth + gap));

    return Math.max(1, Math.min(itemCount, measuredColumns || 1));
};

export const getBalancedIconTileRowSizes = (itemCount: number, maxColumns: number) => {
    if (itemCount <= 0) return [];

    const columns = Math.max(1, Math.min(itemCount, maxColumns));

    if (columns <= 1) return Array.from({ length: itemCount }, () => 1);
    if (itemCount <= columns) return [itemCount];

    const fullRows = Math.floor(itemCount / columns);
    const remainder = itemCount % columns;

    if (remainder === 0) return Array.from({ length: fullRows }, () => columns);
    if (remainder === 1 && columns > 2) {
        return [
            ...Array.from({ length: Math.max(0, fullRows - 1) }, () => columns),
            columns - 1,
            2,
        ];
    }

    return [...Array.from({ length: fullRows }, () => columns), remainder];
};

const getDefaultIconTileMaxColumns = (itemCount: number) => {
    if (itemCount <= 0) return 1;

    return Math.min(itemCount, ICON_TILE_DEFAULT_COLUMNS);
};

const chunkIconTiles = (items: IconTileType[], rowSizes: number[]) => {
    const rows: IconTileType[][] = [];
    let startIndex = 0;

    rowSizes.forEach((rowSize) => {
        rows.push(items.slice(startIndex, startIndex + rowSize));
        startIndex += rowSize;
    });

    return rows;
};

const IconTiles = ({
    dark,
    content,
    titleClasses = '',
    subtitleClass = '',
    titleSal,
    subtitleSal,
    iconSal,
}: IconTilesProps) => {
    const tilesRef = useRef<HTMLDivElement | null>(null);
    const [maxColumns, setMaxColumns] = useState(() =>
        getDefaultIconTileMaxColumns(content.length),
    );
    const dynamicTileClasses = dark
        ? 'sm:bg-transparent bg-black text-white'
        : 'sm:bg-transparent bg-white';
    const rowSizes = useMemo(
        () => getBalancedIconTileRowSizes(content.length, maxColumns),
        [content.length, maxColumns],
    );
    const rows = useMemo(() => chunkIconTiles(content, rowSizes), [content, rowSizes]);

    useEffect(() => {
        if (!content.length) return;

        const tilesElement = tilesRef.current;
        if (!tilesElement) return;

        let frameId = 0;

        const updateColumnCount = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const styles = window.getComputedStyle(tilesElement);
                const gap =
                    Number.parseFloat(styles.columnGap || styles.gap || '') ||
                    Number.parseFloat(styles.gap || '') ||
                    ICON_TILE_DEFAULT_GAP;
                const availableWidths = [
                    tilesElement.clientWidth,
                    tilesElement.getBoundingClientRect().width,
                    tilesElement.parentElement?.clientWidth,
                ].filter((width): width is number => typeof width === 'number' && width > 0);
                const containerWidth = availableWidths.length ? Math.min(...availableWidths) : 0;
                const nextMaxColumns = getIconTileMaxColumns(containerWidth, content.length, gap);

                setMaxColumns((currentMaxColumns) =>
                    currentMaxColumns === nextMaxColumns ? currentMaxColumns : nextMaxColumns,
                );
            });
        };

        updateColumnCount();

        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateColumnCount) : null;
        observer?.observe(tilesElement);
        window.addEventListener('resize', updateColumnCount);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer?.disconnect();
            window.removeEventListener('resize', updateColumnCount);
        };
    }, [content.length]);

    return (
        <div ref={tilesRef} className="flex w-full flex-col items-center gap-5 xs:gap-6 lg:gap-8">
            {rows.map((row, rowIndex) => (
                <div
                    key={row.map(({ id }) => id).join('-') || rowIndex}
                    className="grid w-full justify-items-center gap-5 xs:gap-6 lg:gap-8"
                    data-icon-tiles-row-size={row.length}
                    style={{
                        gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                    }}
                >
                    {row.map(({ description, title, id, image }: IconTileType) => (
                        <div
                            key={id}
                            className={`${dynamicTileClasses} grid min-h-[210px] w-full max-w-[240px] grid-rows-[auto_auto_1fr] content-start justify-items-center gap-3 rounded-lg p-4 text-center shadow-md transition-transform hover:scale-[1.02] sm:min-h-[220px] sm:p-4 sm:shadow-none`}
                        >
                            <div
                                className="flex h-16 w-16 items-center justify-center xs:h-[72px] xs:w-[72px] sm:h-20 sm:w-20 lg:h-24 lg:w-24"
                                {...iconSal}
                            >
                                <img
                                    src={image.url}
                                    title={image.alt}
                                    alt={image.alt}
                                    loading="lazy"
                                    className="block h-full w-full object-contain"
                                />
                            </div>

                            <h5
                                className={`min-w-0 max-w-full break-words hyphens-auto leading-tight ${titleClasses}`}
                                {...titleSal}
                            >
                                {title}
                            </h5>

                            {description && (
                                <h6
                                    className={`min-w-0 max-w-full break-words hyphens-auto leading-relaxed ${subtitleClass}`}
                                    {...subtitleSal}
                                >
                                    {description}
                                </h6>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default IconTiles;
